import { useEffect, useRef, useState } from "react";
import type { Question } from "../api";
import { Check, Diamond, Warn } from "./icons";

/** How long the tick stays visible before advancing. Long enough to register
 *  the selection, short enough not to feel like waiting. */
const ADVANCE_MS = 280;

interface Props {
  question: Question;
  totalQuestions: number;
  busy: boolean;
  onAnswer: (optionId: string) => void;
  onBack: () => void;
  canGoBack: boolean;
  isLast: boolean;
}

export function QuestionScreen({
  question,
  totalQuestions,
  busy,
  onAnswer,
  onBack,
  canGoBack,
  isLast,
}: Props) {
  const [selected, setSelected] = useState<string | null>(question.selected_option_id);
  const [showHint, setShowHint] = useState(false);
  const advanceTimer = useRef<number | null>(null);

  // A new question means a fresh selection and a cancelled pending advance —
  // otherwise a timer from the previous screen fires against this one.
  useEffect(() => {
    setSelected(question.selected_option_id);
    setShowHint(false);
    return () => {
      if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    };
  }, [question.id, question.selected_option_id]);

  /** Auto-advance is pointer-only.
   *
   *  Arrow keys move through a radio group and check each option on the way
   *  past. Advancing on selection would rip the page away from a keyboard user
   *  who is still reading the choices, so they get the Continue button instead.
   *  These are <button role="radio"> rather than real radios precisely so that
   *  arrow keys don't auto-select at all — but the rule stands either way. */
  function pick(optionId: string, viaPointer: boolean) {
    if (busy) return;
    setSelected(optionId);
    setShowHint(false);

    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    if (!viaPointer) return;

    advanceTimer.current = window.setTimeout(() => onAnswer(optionId), ADVANCE_MS);
  }

  function handleContinue() {
    if (!selected) {
      setShowHint(true);
      return;
    }
    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    onAnswer(selected);
  }

  return (
    <>
      <div className="cat-eyebrow">
        <Diamond />
        {question.category}
      </div>

      <fieldset className="q-single">
        <legend className="q-single-text">{question.text}</legend>
        <div className="opts" role="radiogroup" aria-label={question.text}>
          {question.options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={selected === option.id}
              className="opt"
              disabled={busy}
              // detail > 0 distinguishes a real click from a keyboard-triggered
              // one (Enter/Space report detail === 0)
              onClick={(e) => pick(option.id, e.detail > 0)}
            >
              <span className="tick">
                <Check />
              </span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </fieldset>

      {showHint && (
        <div className="hint">
          <Warn />
          <span>Choose an option to continue.</span>
        </div>
      )}

      <div className="nav">
        <button className="btn btn-ghost" onClick={onBack} disabled={!canGoBack || busy}>
          Back
        </button>
        <div className="spacer" />
        <button className="btn" onClick={handleContinue} disabled={busy}>
          {busy ? "Saving…" : isLast ? "See my results" : "Continue"}
        </button>
      </div>

      <p className="sr" aria-live="polite">
        Question {question.position} of {totalQuestions}
      </p>
    </>
  );
}
